import React, { useState, useEffect } from 'react';
import Modal from '@/components/ui/Modal';
import Input from '@/components/ui/Input';
import Button from '@/components/ui/Button';

export interface ChangeUsernameModalProps {
    isOpen: boolean;
    onClose: () => void;
    currentUsername: string;
    onSubmit: (newUsername: string) => Promise<void>;
    isLoading?: boolean;
    error?: string;
}

const ChangeUsernameModal: React.FC<ChangeUsernameModalProps> = ({
    isOpen,
    onClose,
    currentUsername,
    onSubmit,
    isLoading = false,
    error,
}) => {
    const [newUsername, setNewUsername] = useState('');
    const [validationError, setValidationError] = useState('');

    // Reset form when modal opens/closes
    useEffect(() => {
        if (isOpen) {
            setNewUsername('');
            setValidationError('');
        }
    }, [isOpen]);

    const validateUsername = (username: string): string => {
        if (!username.trim()) {
            return 'Username is required';
        }

        if (username.length < 3) {
            return 'Username must be at least 3 characters long';
        }

        if (username.length > 30) {
            return 'Username must be less than 30 characters';
        }

        // Basic username format validation (alphanumeric, underscore, hyphen)
        const usernameRegex = /^[a-zA-Z0-9_-]+$/;
        if (!usernameRegex.test(username)) {
            return 'Username can only contain letters, numbers, underscores, and hyphens';
        }

        if (username === currentUsername) {
            return 'New username must be different from current username';
        }

        return '';
    };

    const handleUsernameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        setNewUsername(value);

        // Clear validation error when user starts typing
        if (validationError) {
            setValidationError('');
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        const validation = validateUsername(newUsername);
        if (validation) {
            setValidationError(validation);
            return;
        }

        try {
            await onSubmit(newUsername);
        } catch (err) {
            // Error handling is managed by parent component
        }
    };

    const handleClose = () => {
        if (!isLoading) {
            onClose();
        }
    };

    const contentStyles: React.CSSProperties = {
        display: 'flex',
        flexDirection: 'column',
        gap: '24px',
    };

    const currentUsernameStyles: React.CSSProperties = {
        display: 'flex',
        flexDirection: 'column',
        gap: '8px',
    };

    const labelStyles: React.CSSProperties = {
        fontSize: '13px',
        fontWeight: 500,
        color: 'var(--text-low_em)',
        margin: 0,
    };

    const currentValueStyles: React.CSSProperties = {
        fontSize: '15px',
        fontWeight: 500,
        color: 'var(--text-high_em)',
        padding: '12px',
        backgroundColor: 'var(--surface-surface_2)',
        borderRadius: '12px',
        border: '1px solid var(--rare-border)',
        margin: 0,
    };

    const buttonGroupStyles: React.CSSProperties = {
        display: 'flex',
        gap: '12px',
        justifyContent: 'flex-end',
        marginTop: '8px',
    };

    const displayError = error || validationError;

    return (
        <Modal
            isOpen={isOpen}
            onClose={handleClose}
            title="Change Username"
            size="md"
            closeOnOverlayClick={!isLoading}
            closeOnEscape={!isLoading}
        >
            <form onSubmit={handleSubmit} style={contentStyles}>
                <div style={currentUsernameStyles}>
                    <label style={labelStyles}>Current Username</label>
                    <div style={currentValueStyles}>{currentUsername}</div>
                </div>

                <Input
                    label="New Username"
                    type="text"
                    value={newUsername}
                    onChange={handleUsernameChange}
                    placeholder="Enter new username"
                    error={displayError}
                    disabled={isLoading}
                    required
                    autoFocus
                />

                <div style={buttonGroupStyles}>
                    <Button
                        type="button"
                        variant="secondary"
                        onClick={handleClose}
                        disabled={isLoading}
                    >
                        Cancel
                    </Button>
                    <Button
                        type="submit"
                        variant="primary"
                        isLoading={isLoading}
                        disabled={!newUsername.trim() || isLoading}
                    >
                        Change Username
                    </Button>
                </div>
            </form>
        </Modal>
    );
};

export default ChangeUsernameModal;
